import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { sendDashboardShareLink, sendDashboardShareImage } from '@/api/insights';
import { sendRequisitionShareLinks } from '@/api/requisitions';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppAuthData } from '@/stores/app/authData';
import { sendTeamInviteEmail, sendTeamOnboardEmail } from '@/api/settings';
import { useDatePicker } from '@/stores/filters/datePicker';
import { getPNGBlob } from '@/helpers/fileDownload';
import { toFileNameFormat, READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import { format } from 'date-fns';
import { useRequisitionReport } from '../requisitions/requisitionReport';

const REPORT_IDS = {
  'demographic-survey-report': 'demographicSurveyReport',
  'compliance-report': 'complianceReportNew',
  'recruiting-performance': 'recruitingPerformanceReport',
  usage: 'usageReport',
  'job-performance-report': 'jobPerformanceReport',
  'content-snapshot': 'contentSnapshot',
  'drop-off': 'dropOffReport',
  'employer-brand-report': 'employerBrandReport',
  'executive-insights': 'executiveInsightsReport',
  'gender-report': 'genderReport',
  'hiring-report': 'hiringReport',
  'inbound-report': 'inboundReport',
  'language-snapshot': 'languageSnapshot',
  'outbound-report': 'outboundReport',
  'overall-report': 'overallReport',
  'pass-through': 'passthroughReport',
  'weekly-snapshot': 'weeklySnapshot',
  'requisition-report': 'requisitionReport',
};

export const useInsightsShare = defineStore('insightsShare', () => {
  const shareConfig = ref({
    shareable: false,
    showReportImageButton: false,
    usersWithAccess: [],
    checkDSRAccess: false,
  });
  const shareInProgress = ref(false);

  const setShareConfig = (config) => {
    shareConfig.value = { ...shareConfig.value, ...config };
  };

  const resetShareConfig = () => {
    shareConfig.value = {
      shareable: false,
      showReportImageButton: false,
      usersWithAccess: [],
      checkDSRAccess: false,
    };
  };

  const updateShareInProgress = (isSharing) => {
    shareInProgress.value = isSharing;
  };

  const shareReportViaEmail = async ({
    userId, note, url, routeName, name, scheduledAt,
  }) => {
    const { reqUuid, title } = storeToRefs(useRequisitionReport());

    const shareEndpoint = routeName === 'requisition-report' ? sendRequisitionShareLinks : sendDashboardShareLink;
    const sharePayload = routeName === 'requisition-report'
      ? {
        userId,
        note,
        reportUrl: url,
        requisition: {
          uuid: reqUuid.value,
          title: title.value,
        },
        scheduledAt,
      }
      : {
        userId,
        note,
        url,
        reportId: REPORT_IDS[routeName],
        scheduledAt,
      };

    try {
      const { success, error, details } = await shareEndpoint(sharePayload);

      if (success && !scheduledAt) {
        useAppSnackbar().addSuccessSnackbar({
          text: `The report was shared with ${name}.`,
        });
      }

      if (!success && error && details) {
        useAppSnackbar().addErrorSnackbar({
          text: `Failed to send email - ${error}`,
        });
      }
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({
        text: `Share failed - ${error.message}`,
      });
      throw error;
    }
  };

  const shareReportImage = async ({
    userId, note, page = 'insights', routeName, name,
  }) => {
    try {
      const { authData } = storeToRefs(useAppAuthData());
      const { dateRange } = storeToRefs(useDatePicker());

      const today = format(new Date(), READABLE_FILE_NAME_FORMAT);
      const dateString = dateRange.value.from && dateRange.value.to
        ? `${toFileNameFormat(dateRange.value.from)}-${toFileNameFormat(dateRange.value.to)}`
        : `${today}`;

      const { blob, title } = await getPNGBlob({
        backgroundColor: '#FAFAFA',
        containerSelector: `[data-png-export=${page}]`,
        style: { margin: '0' },
        fileName: `${authData.value.projectName}-${routeName}-${dateString}`,
      });

      const imageFile = new File([blob], title, {
        type: 'image/png',
      });

      const formData = new FormData();

      formData.append('userId', userId);
      formData.append('note', note);
      formData.append('attachment', imageFile);
      formData.append('reportId', REPORT_IDS[routeName]);

      const { success, error, details } = await sendDashboardShareImage(formData);

      if (success) {
        useAppSnackbar().addSuccessSnackbar({
          text: `The report image was shared with ${name}.`,
        });
      }

      if (!success && error && details) {
        useAppSnackbar().addErrorSnackbar({
          text: `Failed to send image - ${error}`,
        });
      }
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({
        text: `Share failed - ${error.message}`,
      });
      throw error;
    }
  };

  const inviteUserToInsights = async ({
    email, id, routeName, name,
  }) => {
    try {
      if (useAppAuthData().authData.integrated) {
        await sendTeamOnboardEmail([id], routeName);
      } else {
        await sendTeamInviteEmail(email, routeName);
      }

      useAppSnackbar().addSuccessSnackbar({
        text: `${name} has been invited to join JobNav to view this report.`,
      });
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({
        text: `Invite failed - ${error.message}`,
      });

      throw error;
    }
  };

  return {
    shareConfig,
    shareInProgress,

    setShareConfig,
    resetShareConfig,
    updateShareInProgress,
    shareReportViaEmail,
    shareReportImage,
    inviteUserToInsights,
  };
});
